import { Alert, Button, Group, SegmentedControl, Stack, Text, Title } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { previewReviewPage } from "@/api/branding";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";

/**
 * Lab portal: the patient page in the lab's saved branding, opened from the report review
 * page (plan B7), so the doctor approving a report can see the view the patient gets.
 *
 * Read only. It shows report-summary's synthetic sample report, never this patient's, in
 * the branding saved for the doctor's own organisation: the BFF takes the organisation from
 * the token and sends no draft, and the orchestrator refuses anything else. The page is
 * HTML carrying lab-controlled text, so it is shown only in a fully sandboxed iframe, on top
 * of the `Content-Security-Policy: sandbox` the server sends it with.
 */
export function PortalBrandedPreviewPage(): JSX.Element {
  const { reportId = "" } = useParams();
  const [language, setLanguage] = useState<"en" | "ar">("en");
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const serial = useRef(0);

  useEffect(() => {
    const mine = ++serial.current;
    setFailed(false);
    previewReviewPage(language)
      .then((text) => {
        if (mine === serial.current) setHtml(text);
      })
      .catch(() => {
        if (mine === serial.current) {
          setHtml(null);
          setFailed(true);
        }
      });
  }, [language]);

  const back = `/portal/reports/${encodeURIComponent(reportId)}`;
  const crumbs = [
    { label: t("portal.reports"), to: "/portal/reports" },
    { label: t("portal.report_review"), to: back },
    { label: t("portal.report_branded_preview_title") },
  ];

  return (
    <AppLayout breadcrumbs={crumbs}>
      <Title order={1}>{t("portal.report_branded_preview_title")}</Title>
      <Stack gap="xs" mt="md">
        <Text size="sm" c="dimmed" data-testid="branded-preview-note">
          {t("portal.report_branded_preview_note")}
        </Text>
        <Group justify="space-between">
          <SegmentedControl
            size="xs"
            value={language}
            onChange={(v) => setLanguage(v as "en" | "ar")}
            data={[
              { value: "en", label: t("branding.preview_english") },
              { value: "ar", label: t("branding.preview_arabic") },
            ]}
          />
          <Button component={Link} to={back} variant="subtle" size="xs">
            {t("portal.report_branded_preview_back")}
          </Button>
        </Group>
        {failed ? (
          <Alert color="orange" p="xs" data-testid="branded-preview-error">
            {t("portal.report_branded_preview_failed")}
          </Alert>
        ) : null}
        {html !== null && !failed ? (
          <iframe
            title={t("portal.report_branded_preview_title")}
            sandbox=""
            srcDoc={html}
            style={{ width: "100%", height: 640, border: "1px solid #ddd", background: "#fff" }}
            data-testid="branded-preview-frame"
          />
        ) : null}
      </Stack>
    </AppLayout>
  );
}
